import { z } from 'zod';
import { pageSchema, paginationQuerySchema, timestampSchema, uuidSchema } from './common';
import { entityAnchorQuerySchema, entityKindSchema } from './entities';

/**
 * What an item used to say (docs/register-collaboration-design.md section 4).
 *
 * A history of edits, keyed by `(entityKind, entityId)` like every other
 * cross-cutting mechanism the platform holds. It is deliberately *not* the
 * audit log, and the four reasons are worth keeping next to the code because
 * the same temptation arrives with every new module:
 *
 *  - audit records **that** an item was updated, not what changed in it;
 *  - audit is read behind `audit.read`, an administrator's permission, while
 *    the history of a risk should be readable by anyone who may read the risk;
 *  - audit rows carry their own retention and redaction, and a history should
 *    live exactly as long as the item it belongs to;
 *  - `annotations.ts` already draws this line -- "audit answers *who did what
 *    to the system*" -- and warns that mixing other questions into it makes it
 *    unusable for investigating access.
 *
 * There is no interface, no registry and no per-kind code here: the generality
 * is one enum column, and the read endpoint authorizes from the
 * `readPermission` the entity catalog already declares.
 */

/* ------------------------------------------------------------ what changed */

/**
 * Values longer than this are stored abbreviated, with `truncated` set.
 *
 * A description is up to 8,000 characters and a rationale 4,000; two copies of
 * either on every save would make the history larger than the register it is a
 * history of. The precedent is `redactMetadata`'s own `MAX_STRING_LENGTH`,
 * which truncates audit metadata at 500 for the same reason -- this is looser
 * because the value here is the point rather than the context.
 */
export const REVISION_VALUE_MAX_LENGTH = 1000;

/**
 * One field's before and after.
 *
 * Values are stored **raw**, exactly as they cross the wire: `"open"` and
 * `"closed"`, not "Avoin" and "Suljettu". A history is a permanent record and
 * translating on the way in would freeze one reader's language into it; the
 * panel renders `t('register.status.open')` at read time instead, in whatever
 * language the reader is using today.
 */
export const fieldChangeSchema = z.object({
  /** `'status'`, `'severity'`, or `'attributes.rationale'` for a declared key. */
  field: z.string().max(120),
  from: z.unknown(),
  to: z.unknown(),
  /** Set when either value was abbreviated to `REVISION_VALUE_MAX_LENGTH`. The
   *  panel says the field changed rather than pretending to show all of it. */
  truncated: z.literal(true).optional(),
});
export type FieldChange = z.infer<typeof fieldChangeSchema>;

/**
 * Abbreviates one stored value.
 *
 * Exported for the diff and for tests; nothing else should need it. Non-strings
 * pass through untouched -- an ordinal and a date are already short, and a
 * truncated number would be a wrong number rather than a shortened one.
 */
export function abbreviateRevisionValue(value: unknown): { value: unknown; truncated: boolean } {
  if (typeof value !== 'string' || value.length <= REVISION_VALUE_MAX_LENGTH) {
    return { value, truncated: false };
  }
  return { value: `${value.slice(0, REVISION_VALUE_MAX_LENGTH)}...`, truncated: true };
}

/**
 * Reads a field out of a record, following one dot for a declared attribute
 * key. Deliberately one level deep: `attributes` is a flat bag of declared
 * scalars by contract (`register.ts`, rule 1), and a general path reader would
 * be inviting a shape that rule forbids.
 */
/**
 * One named field, possibly nested one level.
 *
 * `object` in, indexed inside. Requiring `Record<string, unknown>` at the
 * boundary is a TypeScript artifact rather than a real constraint -- a
 * generated Prisma row has every field this reads and no index signature, so
 * every caller holding one was writing `as unknown as Record<string, unknown>`
 * to get past it. That double cast is the loudest kind of unsound, and it sat
 * at the call site where it could quietly outlive the reason for it. One
 * narrowing here, where the function already does exactly this for the nested
 * case, replaces it.
 */
function readField(record: object, field: string): unknown {
  const indexed = record as Record<string, unknown>;
  const dot = field.indexOf('.');
  if (dot === -1) return indexed[field];
  const container = indexed[field.slice(0, dot)];
  if (!container || typeof container !== 'object') return undefined;
  return (container as Record<string, unknown>)[field.slice(dot + 1)];
}

/**
 * Two values are the same edit-wise.
 *
 * `null` and `undefined` are one state, not two: a field cleared by omission
 * and a field cleared by an explicit null are the same event to a reader, and
 * recording "not set → not set" is how a history fills up with rows that say
 * nothing. Everything else compares by JSON, which is exact for the scalars
 * that reach here and stable for the declared attribute bag.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if ((a ?? null) === null && (b ?? null) === null) return true;
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * What changed between two versions of a record, over a declared field list.
 *
 * Pure, and the only thing in this file that does any work. The caller supplies
 * the fields because only the owning module knows them -- the register passes
 * its writable columns plus `attributes.<key>` for every key the kind's schema
 * declares, so a new attribute in the catalog is tracked without a line being
 * written here either.
 *
 * A field absent from `after` is *not* a change to null: a `PATCH` carries only
 * what the client sent, and treating an unmentioned field as cleared would
 * record edits nobody made. The caller passes the merged "after" state when it
 * wants the full picture, which is what the register does.
 */
export function diffRecords(
  before: object,
  after: object,
  fields: readonly string[],
): FieldChange[] {
  const changes: FieldChange[] = [];

  for (const field of fields) {
    const to = readField(after, field);
    if (to === undefined) continue;
    const from = readField(before, field);
    if (sameValue(from, to)) continue;

    const left = abbreviateRevisionValue(from);
    const right = abbreviateRevisionValue(to);
    changes.push({
      field,
      from: left.value ?? null,
      to: right.value ?? null,
      ...(left.truncated || right.truncated ? { truncated: true as const } : {}),
    });
  }

  return changes;
}

/* ------------------------------------------------------------------- wire */

/**
 * Creation is recorded too, with no changes.
 *
 * A history whose first line is an edit leaves the reader wondering what the
 * item said before it, and "it did not exist" is the answer. Deletion is not
 * recorded here: the row is gone and its revisions go with it (the purge
 * trigger), and who deleted what is exactly the question the audit log is for.
 */
export const revisionOperationSchema = z.enum(['created', 'updated']);
export type RevisionOperation = z.infer<typeof revisionOperationSchema>;

export const entityRevisionSchema = z.object({
  id: uuidSchema,
  entityKind: entityKindSchema,
  entityId: uuidSchema,
  operation: revisionOperationSchema,
  changes: z.array(fieldChangeSchema),
  /** The item version this edit produced, so a revision can be lined up
   *  against the optimistic-locking version a client is holding. */
  itemVersion: z.number().int().nonnegative(),
  actorUserId: uuidSchema.nullable(),
  /**
   * Who, denormalized at write time.
   *
   * The same choice `audit_event` makes and for the same reason: a history that
   * becomes "«unknown» raised the severity" once somebody leaves the company
   * has lost the part people read it for.
   */
  actorLabel: z.string(),
  createdAt: timestampSchema,
});
export type EntityRevision = z.infer<typeof entityRevisionSchema>;

export const entityRevisionPageSchema = pageSchema(entityRevisionSchema);

export const revisionListQuerySchema = paginationQuerySchema
  .extend(entityAnchorQuerySchema.shape)
  .strict();
export type RevisionListQuery = z.infer<typeof revisionListQuerySchema>;
